'use strict';

export default class InfiniteScroll {
    constructor(container, settings) {
        this.data = {
            container: container,
            targetContainer: null,
            actions: null,
            loadMoreButton: null,
            loadingAnimation: null,
            errorContainer: null,
            fetchRequest: null,
            scrollTicking: false,
            ready: true,
            observer: null,
            // Prefetch buffers
            prefetched: {}, // page -> data
            prefetching: {} // page -> true while in-flight
        };

        this.settings = {
            url: '',
            page: 1,

            timeoutReload: true,
            offsetBottom: 50,
            // IntersectionObserver: start loading earlier before reaching the button
            ioRootMarginBottom: 1000, // px
            loadOnScrolling: true,
            targetSelector: '',
            targetClasses: '',
            loadMoreText: 'Load more',
            loadMoreClasses: 'load-more btn btn-primary',
            loadingText: 'Loading...',
            loadingClasses: 'loading spinner1',
            errorClasses: 'error',
            request: {},
            beforeRequest: null,
            beforeAppendResults: null,
            appendResults: null,
            afterAppendResults: null,
            // Speed tweaks
            initialPages: 2, // try to fill viewport fast
            initialViewportMultiple: 1.25, // fill until container bottom passes 1.25x viewport
            prefetch: true, // fetch next page(s) in background
            prefetchPages: 1, // how many pages to look ahead
            eagerImagesPerAppend: 4, // force-load a few images of the next chunk
            // Optional DOM windowing (off by default). Set to a positive integer to enable.
            maxItems: 0 // e.g. 300 to cap list and remove oldest when exceeded
        }
        if (settings) {
            this.settings = {...this.settings, ...settings};
        }

        this.createTargetContainer();
        this.createActionBar();
        this.bindObserverOrFallback();
        // Fast boot: load a couple of pages to avoid visible latency
        this.bootstrapInitial().then(() => {
            // After boot, ensure IO or fallback continues as usual
            this.checkAndLoadItems(false);
        });

        return this;
    }

    async bootstrapInitial() {
        // Load until we either reached min pages or filled the viewport adequately
        const minPages = Math.max(1, this.settings.initialPages | 0);
        const maxLoops = minPages + 3; // cap to avoid runaway
        let loops = 0;
        while (this.data.ready === true && loops < maxLoops) {
            loops++;
            await this.loadItems({ reason: 'bootstrap' });
            const bottom = this.data.targetContainer.getBoundingClientRect().bottom;
            const vp = window.innerHeight || document.documentElement.clientHeight || 800;
            const filled = bottom > vp * this.settings.initialViewportMultiple;
            if (loops >= minPages && filled) break;
        }
        // Kick off prefetch for the next pages once bootstrapped
        this.schedulePrefetch();
    }

    bindObserverOrFallback() {
        // Prefer IntersectionObserver for efficiency; fallback to scroll listener
        const instance = this;
        const supportsIO = typeof window !== 'undefined' && 'IntersectionObserver' in window && 'IntersectionObserverEntry' in window;

        if (supportsIO) {
            try {
                const bottom = (this.settings.ioRootMarginBottom != null ? this.settings.ioRootMarginBottom : this.settings.offsetBottom) | 0;
                const rootMargin = `0px 0px ${Math.max(0, bottom)}px 0px`;
                this.data.observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            // Respect loadOnScrolling flag; trigger load directly to avoid extra checks
                            if (instance.settings.loadOnScrolling && instance.data.ready === true) {
                                instance.loadItems({ reason: 'io' });
                            }
                        }
                    });
                }, { root: null, threshold: 0, rootMargin });
                if (this.data.loadMoreButton) {
                    this.data.observer.observe(this.data.loadMoreButton);
                }
                return;
            } catch (e) {
                // If IO instantiation fails for any reason, fall back to scroll
                console.warn('IntersectionObserver setup failed, falling back to scroll listener', e);
            }
        }

        // Fallback: scroll listener (older browsers)
        document.addEventListener('scroll', function () {
            if (!instance.data.scrollTicking) {
                window.requestAnimationFrame(function () {
                    instance.data.scrollTicking = false;
                    instance.checkAndLoadItems(false);
                });
                instance.data.scrollTicking = true;
            }
        }, { passive: true });
    }

    checkAndLoadItems(forceLoading) {
        if (typeof forceLoading === 'undefined') {
            forceLoading = false;
        }

        // If using IntersectionObserver, it will call this method when intersecting.
        // For fallback (or button click/force), we keep boundingClientRect check here.
        let shouldLoad = forceLoading;
        if (!shouldLoad) {
            // Only compute if no IO or when using fallback
            const usingFallback = !this.data.observer;
            if (usingFallback && this.settings.loadOnScrolling) {
                let posBinder = parseInt(this.data.loadMoreButton.getBoundingClientRect().top);
                let windowBottom = window.scrollY + window.innerHeight;
                let binderUpperWindow = (posBinder <= 0);
                let binderInWindow = (posBinder > 0 && windowBottom > (posBinder - this.settings.offsetBottom));
                shouldLoad = (binderUpperWindow || binderInWindow);
            }
        }

        if (shouldLoad && this.data.ready === true) {
            this.loadItems({ reason: forceLoading ? 'force' : 'fallback' });
        }
    }

    createDivElement(text, classes) {
        let element = document.createElement('div');
        if (classes) {
            classes.split(' ').forEach(className => {
                className = className.trim();
                className && element.classList.add(className);
            });
        }
        element.innerHTML = text;
        return element;
    }

    createTargetContainer() {
        if (this.settings.targetSelector) {
            this.data.targetContainer = this.data.container.querySelector(this.settings.targetSelector);
        }
        if (!this.data.targetContainer) {
            this.data.targetContainer = this.createDivElement('', this.settings.targetClasses);
            this.data.container.append(this.data.targetContainer);
        }
    }

    createActionBar() {
        let instance = this;
        let actions = document.createElement('div');
        actions.classList.add('actions');

        this.data.loadMoreButton = this.createDivElement(this.settings.loadMoreText, this.settings.loadMoreClasses);
        actions.append(this.data.loadMoreButton);

        this.data.loadingAnimation = this.createDivElement(this.settings.loadingText, this.settings.loadingClasses);
        actions.append(this.data.loadingAnimation);

        this.data.errorContainer = this.createDivElement('', this.settings.errorClasses);
        this.data.errorContainer.style.display = 'none';
        actions.append(this.data.errorContainer);

        this.data.actions = actions;
        this.data.container.append(this.data.actions);

        this.data.loadMoreButton.addEventListener('click', function (event) {
            event.preventDefault();
            event.stopPropagation();
            instance.checkAndLoadItems(true);
        });
    }

    isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

    resolveAfterSeconds(seconds) {
        console.log('Resolve after ' + seconds +  ' seconds:', 'calling');
        let result = new Promise(resolve => {
            setTimeout(() => {
                resolve('resolved');
            }, seconds * 1000);
        });
        console.log('Resolve after ' + seconds +  ' seconds:', result);
        return result;
    }

    async fetchData(url = '', data = {}) {
        // const justWait = await this.resolveAfterSeconds(1);

        let request = {
            method: 'GET',
        };
        request = {...request, ...this.settings.request};

        if (request.method.toLowerCase() === 'get') {
            // @todo url parse query parameter
            url += (url.indexOf('?') > -1 ? '&' : '?') + new URLSearchParams(data).toString();
        }

        if (request.method.toLowerCase() === 'post') {
            if (request.headers['Content-Type'] === 'application/json') {
                request.body = JSON.stringify(data);
            } else {
                request.body = new URLSearchParams(data).toString();
            }
        }

        return await fetch(url, request).then((response) => {
            if (response.headers.get('Content-Type')) {
                let contentTypeArray = response.headers.get('Content-Type').split(';');
                if (contentTypeArray[0] === 'application/json') {
                    return response.json();
                }
            }
            return response.text();
        }).catch(reason => {
            console.error('fetch error', reason);
            this.showError('fetch error' + reason);
        });
    }

    async loadItems(specialArguments) {
        const instance = this;
        this.showLoadingAnimation();

        const pageToLoad = this.settings.page;

        const usePrefetch = !!this.settings.prefetch && this.data.prefetched[pageToLoad] !== undefined;
        const handleData = (data) => {
            instance.hideLoadingAnimation();
            instance.settings.page += 1;

            if (this.isFunction(this.settings.beforeAppendResults)) {
                instance.settings.beforeAppendResults(this, data);
            }

            if (this.isFunction(this.settings.appendResults)) {
                instance.settings.appendResults(this, data);
            } else if (typeof data === 'object') {
                instance.showError('Content is a object!<br>Provide your own appendResults() in settings!');
                instance.disableInfiniteScrolling();
                return;
            } else if (typeof data === 'string') {
                const html = data.trim();
                if (html === '') {
                    instance.showError('Content empty!');
                }
                const fragment = this.createDivElement(html, '');

                // Eager-load a few images to avoid visible delay
                this.eagerPrimeImages(fragment, this.settings.eagerImagesPerAppend | 0);

                if (fragment.children.length > 0) {
                    Array.from(fragment.children).forEach(child => instance.data.targetContainer.append(child));
                } else {
                    instance.data.targetContainer.append(fragment);
                }
            } else {
                instance.showError('Unknown content!<br>Provide your own appendResults() in settings!');
                instance.disableInfiniteScrolling();
                return;
            }

            if (this.isFunction(this.settings.afterAppendResults)) {
                instance.settings.afterAppendResults(this, data, specialArguments);
            }

            instance.data.ready = true;

            // Prune DOM if a max window is configured
            this.pruneIfNeeded();

            // Schedule prefetch for upcoming pages
            this.schedulePrefetch();

            if (this.settings.timeoutReload) {
                setTimeout(() => instance.checkAndLoadItems(false), 500);
            }
        };

        if (usePrefetch) {
            const data = this.data.prefetched[pageToLoad];
            delete this.data.prefetched[pageToLoad];
            handleData(data);
            return Promise.resolve();
        }

        let fetchData = { page: pageToLoad };
        if (this.isFunction(this.settings.beforeRequest)) {
            fetchData = this.settings.beforeRequest(this, fetchData);
        }

        this.data.fetchRequest = this.fetchData(this.settings.url, fetchData)
            .then(handleData)
            .catch(reason => {
                console.error('Loading error:', reason);
                this.showError('Loading error: ' + reason);
                this.hideLoadingAnimation();
                this.disableInfiniteScrolling();
            });

        return this.data.fetchRequest;
    }

    pruneIfNeeded() {
        const max = this.settings.maxItems | 0;
        if (!max || !this.data.targetContainer) return;
        try {
            const items = this.data.targetContainer.children;
            const excess = items.length - max;
            if (excess > 0) {
                for (let i = 0; i < excess; i++) {
                    this.data.targetContainer.removeChild(items[0]);
                }
            }
        } catch (e) {}
    }

    schedulePrefetch() {
        if (!this.settings.prefetch) return;

        // Avoid prefetch on extremely slow or data-saver connections
        try {
            const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
            if (conn && (conn.saveData || (conn.effectiveType && /2g/i.test(conn.effectiveType)))) {
                return;
            }
        } catch (e) {}

        for (let i = 0; i < (this.settings.prefetchPages | 0); i++) {
            const page = this.settings.page + i; // lookahead
            if (this.data.prefetched[page] !== undefined || this.data.prefetching[page]) continue;
            // Build request data the same way as load
            let fetchData = { page };
            if (this.isFunction(this.settings.beforeRequest)) {
                fetchData = this.settings.beforeRequest(this, fetchData);
            }
            this.data.prefetching[page] = true;
            this.fetchData(this.settings.url, fetchData)
                .then(data => { this.data.prefetched[page] = data; })
                .catch(() => { /* ignore prefetch errors */ })
                .finally(() => { delete this.data.prefetching[page]; });
        }
    }

    eagerPrimeImages(rootNode, take) {
        const n = take | 0;
        if (n <= 0 || !rootNode) return;
        try {
            const imgs = rootNode.querySelectorAll('img');
            for (let i = 0, c = 0; i < imgs.length && c < n; i++) {
                const img = imgs[i];
                // Only prime images that would otherwise delay
                if (img.hasAttribute('loading') && img.getAttribute('loading') === 'lazy') {
                    img.removeAttribute('loading');
                    img.setAttribute('decoding', 'async');
                    img.setAttribute('fetchpriority', 'high');
                    c++;
                }
            }
        } catch (e) {}
    }

    showLoadingAnimation() {
        this.data.loadingAnimation.style.display = 'inline-block';
        this.data.loadMoreButton.style.display = 'none';
        this.data.ready = false;
    }

    hideLoadingAnimation() {
        this.data.loadingAnimation.style.display = 'none';
        this.data.loadMoreButton.style.display = 'inline-block';
        this.data.ready = true;
    }

    enableInfiniteScrolling() {
        this.data.actions.style.display= 'block';
        this.data.ready = true;
        if (this.data.observer && this.data.loadMoreButton) {
            try { this.data.observer.observe(this.data.loadMoreButton); } catch (e) {}
        }
    }

    disableInfiniteScrolling() {
        // this.data.actions.style.display= 'none';
        this.data.loadingAnimation.style.display = 'none';
        this.data.loadMoreButton.style.display = 'none';
        this.data.ready = false;
        if (this.data.observer && this.data.loadMoreButton) {
            try { this.data.observer.unobserve(this.data.loadMoreButton); } catch (e) {}
        }
    }

    showError(message) {
        this.data.errorContainer.innerText = message;
        this.data.errorContainer.style.display = 'block';
    }

    hideError() {
        this.data.errorContainer.style.display = 'none';
        this.data.errorContainer.innerText = '';
    }

    reset() {
        if (this.data.fetchRequest.hasOwnProperty('abort')) {
            this.data.fetchRequest.abort();
        }

        this.disableInfiniteScrolling();
        this.hideError();

        this.data.targetContainer.innerHTML = '';
        this.settings.page = 1;
    this.data.prefetched = {};
    this.data.prefetching = {};

    this.enableInfiniteScrolling();
    // Re-attach observer to loadMoreButton (in case UI changed)
        if (this.data.observer && this.data.loadMoreButton) {
            try { this.data.observer.unobserve(this.data.loadMoreButton); } catch (e) {}
            try { this.data.observer.observe(this.data.loadMoreButton); } catch (e) {}
        }
    // Fast boot after reset as well
    this.bootstrapInitial().then(() => this.checkAndLoadItems(false));
    }

    /*var requestData;
    resetAndReload() {
        if (requestData.hasOwnProperty('abort')) {
            requestData.abort();
        }
        $object.items.empty();
        options.currentPage = 1;
        options.ready = true;
        checkAndLoadItems(false);
    };*/


    /*getParameterByName(name, url) {
        name = name.replace(/[\[\]]/g, "\\$&");
        var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)");
        var results = regex.exec(url);
        if (!results) {
            return null;
        }
        if (!results[2]) {
            return '';
        }
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }*/
}
